import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Building, 
  Building2,
  BookOpen,
  Calendar, 
  DollarSign,
  Image as ImageIcon, 
  Users,
  FileText, 
  Upload,
  MessageSquare,
  UserPlus,
  ChevronDown
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import UnifiedDashboardLayout from './UnifiedDashboardLayout';
import { saveOrganizationSlug, getSavedOrganizationSlug } from './curator/CuratorLandingPage';


const CuratorAdministrativeScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [galleryAccess, setGalleryAccess] = useState([]);
  const [organizationMemberships, setOrganizationMemberships] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get current user, gallery access, and organization memberships
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        
        if (user) {
          // Fetch gallery access and organization memberships in parallel
          const [galleryResult, orgResult] = await Promise.all([
            // Get gallery access with gallery details
            supabase
              .from('gallery_access')
              .select(`
                *,
                galleries:gallery_id (
                  gallery_id,
                  name,
                  address,
                  description,
                  type
                )
              `)
              .eq('user_uuid', user.id)
              .eq('is_active', true),
            
            // Get organization memberships with organization details
            supabase
              .from('organization_members')
              .select(`
                *,
                organizations:organization_id (
                  organization_id,
                  name,
                  slug,
                  description,
                  logo,
                  org_type
                )
              `)
              .eq('user_uuid', user.id)
              .eq('is_active', true)
          ]);

          if (galleryResult.error) {
            console.error('Error fetching gallery access:', galleryResult.error);
          } else {
            setGalleryAccess(galleryResult.data || []);
          }

          if (orgResult.error) {
            console.error('Error fetching organization memberships:', orgResult.error);
          } else {
            setOrganizationMemberships(orgResult.data || []);
            
            // Check if URL has an organization slug and match it
            const orgMatch = location.pathname.match(/\/curator\/organization\/([^/]+)/);
            if (orgMatch) {
              const urlSlug = orgMatch[1];
              const matchedOrg = orgResult.data?.find(m => m.organizations?.slug === urlSlug);
              if (matchedOrg) {
                setSelectedOrganization(matchedOrg.organizations);
              } else if (orgResult.data && orgResult.data.length > 0) {
                setSelectedOrganization(orgResult.data[0].organizations);
              }
            } else if (orgResult.data && orgResult.data.length > 0) {
              // No org in URL, check localStorage for saved preference
              const savedSlug = getSavedOrganizationSlug();
              if (savedSlug) {
                const savedOrg = orgResult.data.find(m => m.organizations?.slug === savedSlug);
                if (savedOrg) {
                  setSelectedOrganization(savedOrg.organizations);
                } else {
                  // Saved slug not found, use first org
                  setSelectedOrganization(orgResult.data[0].organizations);
                }
              } else {
                // No saved preference, use first org
                setSelectedOrganization(orgResult.data[0].organizations);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    getCurrentUser();
  }, [location.pathname]);

  // Sync selectedOrganization with URL when navigating between orgs
  useEffect(() => {
    if (organizationMemberships.length === 0) return;
    
    const orgMatch = location.pathname.match(/\/curator\/organization\/([^/]+)/);
    if (orgMatch) {
      const urlSlug = orgMatch[1];
      const matchedOrg = organizationMemberships.find(m => m.organizations?.slug === urlSlug);
      if (matchedOrg && matchedOrg.organizations?.organization_id !== selectedOrganization?.organization_id) {
        console.log('URL org changed, updating selectedOrganization:', matchedOrg.organizations);
        setSelectedOrganization(matchedOrg.organizations);
      }
    }
  }, [location.pathname, organizationMemberships]);
  if (loading) {
    return (
      <UnifiedDashboardLayout
        dashboardType="curator"
        navItems={[]}
        logoText="Curator Studio"
        logoIcon={Building}
        initialSidebarOpen={false}
        context={{ user, galleryAccess, organizationMemberships }}
      >
        <div className="curator-studio-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your curator dashboard...</p>
          </div>
        </div>
      </UnifiedDashboardLayout>
    );
  }

  // Check if user has gallery access OR organization membership
  const hasAccess = (galleryAccess && galleryAccess.length > 0) || (organizationMemberships && organizationMemberships.length > 0);
  
  if (!hasAccess) {
    return (
      <UnifiedDashboardLayout
        dashboardType="curator"
        navItems={[]}
        logoText="Curator Studio"
        logoIcon={Building}
        initialSidebarOpen={false}
        context={{ user, galleryAccess, organizationMemberships }}
      >
        <div className="curator-studio-content">
          <div className="no-access-state">
            <Building className="no-access-icon" />
            <h2>No Curator Access</h2>
            <p>You don't have curator access to any galleries or organizations yet. Please contact an administrator to request access.</p>
            <Link to="/dashboard-selector" className="back-to-dashboard">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </UnifiedDashboardLayout>
    );
  }

  // Nav items vary by organization type
  const isPrintService = selectedOrganization?.org_type === 'print_service';
  const navItems = isPrintService
    ? [
        { name: 'Collections', href: '/curator/collections', icon: BookOpen },
        { name: 'Service Orders', href: '/curator/service-orders', icon: FileText },
        { name: 'Customers', href: '/curator/customers', icon: Users },
        { name: 'Communications', href: '/curator/communications', icon: MessageSquare },
      ]
    : [
        { name: 'Exhibitions & Collections', href: '/curator/collections', icon: BookOpen },
        { name: 'Artists', href: '/curator/artists', icon: Users },
        { name: 'Artworks', href: '/curator/artworks', icon: ImageIcon },
        { name: 'Contacts', href: '/curator/contacts', icon: UserPlus },
        { name: 'Communications', href: '/curator/communications', icon: MessageSquare },
        { name: 'Billing', href: '/curator/billing', icon: DollarSign },
      ];

  // Extract organizations from memberships for the avatar dropdown
  const organizations = organizationMemberships
    .map(m => m.organizations)
    .filter(Boolean);

  // Determine if we're on the landing/selection page (no org selected yet)
  const isOnLandingPage = location.pathname === '/curator' || location.pathname === '/curator/' || location.pathname === '/curator/dashboard';

  // Handler for organization selection from avatar dropdown
  const handleSelectOrganization = (org) => {
    console.log('Organization selected from dropdown:', org);
    const previousOrg = selectedOrganization;
    setSelectedOrganization(org);
    
    // Save selection to localStorage for persistence
    if (org?.slug) {
      saveOrganizationSlug(org.slug);
    }
    
    const currentPath = location.pathname;
    
    // Check if we're on an organization detail page
    const orgDetailMatch = currentPath.match(/^\/curator\/organization\/([^/]+)(\/team)?$/);
    if (orgDetailMatch && org?.slug) {
      // Navigate to the new organization's page (preserve /team if present)
      const teamSuffix = orgDetailMatch[2] || '';
      const newPath = `/curator/organization/${org.slug}${teamSuffix}`;
      console.log('Navigating to new organization page:', newPath);
      navigate(newPath);
      return;
    }

    // If switching between different org types, redirect to the new org's default page
    const prevType = previousOrg?.org_type || 'gallery';
    const newType = org?.org_type || 'gallery';
    if (prevType !== newType) {
      const defaultPage = newType === 'print_service' ? '/curator/service-orders' : '/curator/collections';
      console.log('Org type changed, navigating to default page:', defaultPage);
      navigate(defaultPage);
      return;
    }
    
    // Navigate to the base section when organization changes
    // If on a detail page (has ID in path), go back to the section root
    const baseSections = ['/curator/collections', '/curator/artists', '/curator/submissions', '/curator/artworks', '/curator/contacts', '/curator/fee-waivers', '/curator/communications', '/curator/billing', '/curator/service-orders', '/curator/customers'];
    
    // Find which section we're in
    const currentSection = baseSections.find(section => currentPath.startsWith(section));
    if (currentSection && currentPath !== currentSection) {
      // We're on a detail page, navigate back to section root
      console.log('Navigating from detail page to section root:', currentSection);
      navigate(currentSection);
    }
  };

  console.log('CuratorAdministrativeScreen: Rendering with:', { 
    user: !!user, 
    galleryAccess: galleryAccess?.length, 
    organizationMemberships: organizationMemberships?.length,
    organizations: organizations?.length,
    selectedOrganization: selectedOrganization?.name,
    isOnLandingPage,
    loading 
  });

  return (
    <UnifiedDashboardLayout
      dashboardType="curator"
      navItems={navItems}
      logoText="Curator Studio"
      logoIcon={Building}
      initialSidebarOpen={false}
      hideSidebar={isOnLandingPage}
      orgLogo={selectedOrganization?.logo}
      orgName={selectedOrganization?.name}
      context={{ 
        user, 
        galleryAccess, 
        organizationMemberships, 
        selectedOrganization,
        organizations,
        onSelectOrganization: handleSelectOrganization
      }}
    >
      <Outlet context={{ user, galleryAccess, organizationMemberships, selectedOrganization }} />
    </UnifiedDashboardLayout>
  );
};

export default CuratorAdministrativeScreen;
